import React, { useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
  Card,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import { COLORS } from 'common/src/utils/colors';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { EmptyState } from '../../ui';

interface CostDataPoint {
  x: string;
  y: [number, number, number, number, number];
}

interface OwnDataPoint {
  x: string;
  y: number;
  label?: string;
  kilos?: number;
}

interface CostChartData {
  others: CostDataPoint[];
  own: OwnDataPoint[];
}

interface CostDispertionChartProps {
  data: CostChartData | null;
  title: string;
  unit: '%' | 'USD/kg' | 'USD/caja';
  separation?: 'none' | 'by_receiver';
  loading: boolean;
  visualization?: 'chart' | 'table';
  othersKg?: number;
  ownKg?: number;
}

const CostDispertionChart: React.FC<CostDispertionChartProps> = ({
  data,
  title,
  unit,
  loading,
  separation = 'none',
  visualization = 'chart',
  othersKg = 0,
  ownKg = 0,
}) => {
  const { darkMode } = useUserStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<
    'label' | 'value' | 'percentile' | 'kilos'
  >('value');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const formatUnit = (val: number) =>
    unit === '%' ? `${val.toFixed(2)}%` : `$${val.toFixed(2)}`;

  const calculatePercentile = (
    value: number | null,
    box: [number, number, number, number, number]
  ) => {
    if (value === null) return null;
    const [min, q1, median, q3, max] = box;
    if (value >= max) return 100;
    if (value <= min) return 0;

    if (value <= q1) {
      return Math.round(((value - min) / (q1 - min)) * 25);
    } else if (value <= median) {
      return Math.round(25 + ((value - q1) / (median - q1)) * 25);
    } else if (value <= q3) {
      return Math.round(50 + ((value - median) / (q3 - median)) * 25);
    } else {
      return Math.round(75 + ((value - q3) / (max - q3)) * 25);
    }
  };

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'boxPlot',
        height: 350,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        animations: { enabled: false },
        events: {
          click: function () {
            // Abrir modal con doble click en cualquier parte del gráfico cuando hay separación por cliente y más de 4
            if (
              separation === 'by_receiver' &&
              data?.own &&
              data.own.length > 4
            ) {
              const now = Date.now();
              const lastClick = (window as any)._lastChartClick || 0;
              if (now - lastClick < 300) {
                // doble click detectado (menos de 300ms)
                setIsModalOpen(true);
              }
              (window as any)._lastChartClick = now;
            }
          },
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
      },
      xaxis: {
        type: 'category',
        categories: [title],
      },
      dataLabels: { enabled: false },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ w }) {
          try {
            // Others stats from first series (boxplot)
            const othersSeries = w.config.series[0]?.data as CostDataPoint[];
            const othersBox = othersSeries && othersSeries[0]?.y;

            // Build market and own in one tooltip
            let marketBlock = '';
            if (othersBox) {
              const [min, q1, median, q3, max] = othersBox as [
                number,
                number,
                number,
                number,
                number
              ];
              const separationText =
                separation !== 'none'
                  ? `<div style="color:#2196F3;font-weight:bold;margin-bottom:6px;">Separado por Cliente</div>`
                  : '';
              marketBlock = `
                <div style="font-weight:bold;margin-bottom:6px;color:${darkMode ? '#ECEDEE' : '#333'};">${title} — Mercado</div>
                ${separationText}
                <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;color:${darkMode ? '#A1A1AA' : '#555'};">
                  <div>Min: <strong>${formatUnit(min)}</strong></div>
                  <div>Q1: <strong>${formatUnit(q1)}</strong></div>
                  <div>Med: <strong>${formatUnit(median)}</strong></div>
                  <div>Q3: <strong>${formatUnit(q3)}</strong></div>
                  <div>Max: <strong>${formatUnit(max)}</strong></div>
                  <div></div>
                </div>`;
            }

            const ownData = (w.config.series[1]?.data || []) as OwnDataPoint[];
            let ownBlock = '';
            if (ownData && ownData.length > 0) {
              const useGrid = separation !== 'none';
              if (useGrid) {
                // Por cliente: 2 por fila, máximo 4 + botón "Ver todos" si hay más de 4
                const displayData =
                  separation === 'by_receiver' && ownData.length > 4
                    ? ownData.slice(0, 4)
                    : ownData;

                const cards = displayData
                  .map((p) => {
                    const pct = othersBox
                      ? calculatePercentile(
                          p.y,
                          othersBox as [number, number, number, number, number]
                        )
                      : null;
                    const id = p.label || 'Propio';
                    return `
                      <div style="background:${darkMode ? 'rgba(255,255,255,0.05)' : '#f8fdf8'};border-left:4px solid ${
                        COLORS.lightGreen
                      };border-radius:6px;padding:4px 6px;font-size:11px;">
                        <div style="color:${
                          COLORS.lightGreen
                        };font-weight:bold;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${id}</div>
                        <div style="margin-top:2px;"><strong>${formatUnit(
                          p.y
                        )}</strong><br><span style="color:${
                      COLORS.cherry
                    };font-weight:bold;font-size:10px;">Percentil: ${
                      pct ?? '-'
                    }%</span></div>
                      </div>`;
                  })
                  .join('');

                // Siempre 2 columnas para etiqueta y cliente
                const template = '1fr 1fr';
                let grid = `<div style="display:grid;grid-template-columns:${template};gap:4px;">${cards}</div>`;

                // Si es por cliente y hay más de 4, agregar indicador "Ver todos"
                if (separation === 'by_receiver' && ownData.length > 4) {
                  const viewAllIndicator = `
                    <div style="margin-top:6px;text-align:center;">
                      <span style="color:#2196F3;font-weight:bold;font-size:11px;">+${
                        ownData.length - 4
                      } más (doble click en gráfico)</span>
                    </div>`;
                  grid += viewAllIndicator;
                }

                ownBlock = `
                  <div style="font-weight:bold;margin:8px 0 6px;color:${darkMode ? '#ECEDEE' : '#333'};">Tus datos</div>
                  ${grid}`;
              } else {
                const rows = ownData
                  .map((p) => {
                    const pct = othersBox
                      ? calculatePercentile(
                          p.y,
                          othersBox as [number, number, number, number, number]
                        )
                      : null;
                    const id = p.label || 'Propio';
                    const percentilRow =
                      pct !== null
                        ? `<div style="margin-top:4px;color:${COLORS.cherry};font-weight:bold;">Percentil: ${pct}%</div>`
                        : '';
                    return `
                      <div style="margin-bottom:4px;padding:6px 8px;background:${darkMode ? 'rgba(255,255,255,0.05)' : '#f8fdf8'};border-radius:6px;border-left:4px solid ${
                        COLORS.lightGreen
                      };">
                        <div style="display:flex;justify-content:space-between;">
                          <span style="color:${
                            COLORS.lightGreen
                          };font-weight:bold;">${id}</span>
                          <span><strong>${formatUnit(p.y)}</strong></span>
                        </div>
                        ${percentilRow}
                      </div>`;
                  })
                  .join('');
                ownBlock = `
                  <div style="font-weight:bold;margin:8px 0 6px;color:${darkMode ? '#ECEDEE' : '#333'};">Tus datos</div>
                  ${rows}`;
              }
            }

            if (!marketBlock && !ownBlock) {
              return '<div style="padding:10px;">Sin datos</div>';
            }
            return `<div style="background:${darkMode ? '#1a1a2e' : '#fff'};padding:10px 12px;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,${darkMode ? '0.4' : '0.15'});font-family:Arial,sans-serif;max-width:420px;font-size:12px;color:${darkMode ? '#ECEDEE' : 'inherit'};">${marketBlock}${ownBlock}</div>`;
          } catch (e) {
            return '<div style="padding:10px;">Error cargando tooltip</div>';
          }
        },
      },
      title: {
        text: title,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: darkMode ? '#ECEDEE' : '#263238',
        },
      },
      // Match sales palette: market (boxplot) in cherry, own (scatter) in light green
      colors: [COLORS.cherry, COLORS.lightGreen],
      stroke: {
        width: 1.5,
        colors: [darkMode ? '#ffffff' : '#000000'],
      },
      grid: {
        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : '#e7e7e7',
        row: {
          colors: [darkMode ? 'rgba(255,255,255,0.03)' : '#f3f3f3', 'transparent'],
          opacity: 0.5,
        },
      },
      yaxis: {
        labels: {
          formatter: (val) =>
            unit === '%' ? `${val.toFixed(2)}%` : `$${val.toFixed(2)}`,
        },
        title: {
          text: `Costo (${unit})`,
        },
      },
      legend: {
        position: 'bottom',
      },
      plotOptions: {
        boxPlot: {
          colors: {
            upper: COLORS.cherry,
            lower: COLORS.cherry,
          },
        },
      } as any,
      markers: {
        size: [0, 6],
        hover: { size: 7 },
      },
    }),
    [title, unit, separation, data, setIsModalOpen, darkMode]
  );

  // Prepare modal data
  const modalData = useMemo(() => {
    if (!data?.own || separation !== 'by_receiver') return [];

    const othersBox = data.others[0]?.y;
    return data.own.map((p) => {
      const pct = othersBox ? calculatePercentile(p.y, othersBox) : null;
      return {
        label: p.label || 'Propio',
        value: p.y,
        percentile: pct,
        kilos: p.kilos || 0,
      };
    });
  }, [data, separation]);

  // Filter and sort modal data
  const filteredAndSortedData = useMemo(() => {
    let filtered = modalData.filter((item) =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'label':
          return sortDirection === 'desc'
            ? b.label.localeCompare(a.label)
            : a.label.localeCompare(b.label);
        case 'value':
          aVal = a.value;
          bVal = b.value;
          break;
        case 'percentile':
          aVal = a.percentile || 0;
          bVal = b.percentile || 0;
          break;
        case 'kilos':
          aVal = a.kilos;
          bVal = b.kilos;
          break;
        default:
          return 0;
      }

      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });

    return filtered;
  }, [modalData, searchTerm, sortBy, sortDirection]);

  const series = useMemo(() => {
    if (!data) return [];
    // Ensure box is visible even when all stats are equal by applying a tiny epsilon
    const adjustedOthers = (data.others || []).map((d) => {
      if (!d?.y) return d;
      const [min, q1, median, q3, max] = d.y;
      const isDegenerate = min === max;
      if (!isDegenerate) return d;
      const base = median || q1 || q3 || min || 0;
      const step =
        unit === '%'
          ? Math.max(base * 0.001, 0.01)
          : Math.max(base * 0.001, 0.01);
      return {
        ...d,
        y: [base - 2 * step, base - step, base, base + step, base + 2 * step],
      } as CostDataPoint;
    });
    return [
      {
        name: 'Industria',
        type: 'boxPlot',
        data: adjustedOthers,
      },
      {
        name: 'Tu Costo',
        type: 'scatter',
        data: data.own,
      },
    ];
  }, [data]);

  if (loading) {
    return (
      <Card className='p-6 h-[400px] flex items-center justify-center'>
        <p className='text-default-400'>Cargando datos del gráfico...</p>
      </Card>
    );
  }

  if (!data || (data.others.length === 0 && data.own.length === 0)) {
    return (
      <Card shadow='sm' className='h-[400px] justify-center p-6'>
        <EmptyState title='No hay datos disponibles para este costo.' />
      </Card>
    );
  }

  // Table view component
  const renderTableView = () => {
    if (!data) return null;

    const othersStats = data.others[0]?.y;
    const ownData = data.own || [];

    return (
      <Card className='p-4'>
        <div className='overflow-x-auto'>
          <table className='min-w-full border-collapse'>
            <thead>
              <tr className='bg-default-100'>
                <th className='border px-4 py-2 text-left'>Estadística</th>
                <th className='border px-4 py-2 text-center'>{title}</th>
              </tr>
            </thead>
            <tbody>
              {/* Own data rows */}
              {ownData.length > 0 && (
                <>
                  <tr className='bg-green-50 dark:bg-green-950'>
                    <td className='border px-4 py-2 font-medium text-green-700 dark:text-green-300'>
                      {separation === 'by_receiver'
                        ? 'Valores Propios (Por Cliente)'
                        : 'Valor Propio'}
                    </td>
                    <td className='border px-4 py-2 text-center font-semibold text-green-700 dark:text-green-300'>
                      {separation === 'none' ? (
                        <strong>{formatUnit(ownData[0].y)}</strong>
                      ) : (
                        <div className='space-y-1'>
                          {ownData.map((item, index) => {
                            const label =
                              separation === 'by_receiver'
                                ? item.label || 'Sin cliente'
                                : 'Propio';
                            return (
                              <div key={index} className='text-sm'>
                                {label}: <strong>{formatUnit(item.y)}</strong>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr className='bg-green-50 dark:bg-green-950'>
                    <td className='border px-4 py-2 font-medium text-green-700 dark:text-green-300'>
                      Percentil Propio
                    </td>
                    <td className='border px-4 py-2 text-center font-semibold text-green-700 dark:text-green-300'>
                      {separation === 'none' ? (
                        othersStats ? (
                          <span className='text-red-600 dark:text-red-400 font-bold'>
                            {calculatePercentile(ownData[0].y, othersStats) ??
                              0}
                            %
                          </span>
                        ) : (
                          '-'
                        )
                      ) : (
                        <div className='space-y-1'>
                          {ownData.map((item, index) => {
                            const label =
                              separation === 'by_receiver'
                                ? item.label || 'Sin cliente'
                                : 'Propio';
                            const percentile = othersStats
                              ? calculatePercentile(item.y, othersStats)
                              : null;
                            return (
                              <div key={index} className='text-sm'>
                                {label}:{' '}
                                <span className='text-red-600 dark:text-red-400 font-bold'>
                                  {percentile ?? 0}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr className='bg-green-50 dark:bg-green-950'>
                    <td className='border px-4 py-2 font-medium text-green-700 dark:text-green-300'>
                      Kilos Propios
                    </td>
                    <td className='border px-4 py-2 text-center font-semibold text-green-700 dark:text-green-300'>
                      {ownKg > 0 ? `${ownKg.toLocaleString('es-CL')} kg` : '-'}
                    </td>
                  </tr>
                </>
              )}

              {/* Others data rows */}
              {othersStats && (
                <>
                  <tr>
                    <td className='border px-4 py-2 font-medium'>Máximo</td>
                    <td className='border px-4 py-2 text-center'>
                      <strong>{formatUnit(othersStats[4])}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td className='border px-4 py-2 font-medium'>Q3</td>
                    <td className='border px-4 py-2 text-center'>
                      <strong>{formatUnit(othersStats[3])}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td className='border px-4 py-2 font-medium'>Mediana</td>
                    <td className='border px-4 py-2 text-center'>
                      <strong>{formatUnit(othersStats[2])}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td className='border px-4 py-2 font-medium'>Q1</td>
                    <td className='border px-4 py-2 text-center'>
                      <strong>{formatUnit(othersStats[1])}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td className='border px-4 py-2 font-medium'>Mínimo</td>
                    <td className='border px-4 py-2 text-center'>
                      <strong>{formatUnit(othersStats[0])}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td className='border px-4 py-2 font-medium'>
                      Kilos Otras
                    </td>
                    <td className='border px-4 py-2 text-center'>
                      {othersKg > 0
                        ? `${othersKg.toLocaleString('es-CL')} kg`
                        : '-'}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  return (
    <>
      {visualization === 'chart' ? (
        <Card className='p-4'>
          <ReactApexChart
            options={chartOptions}
            series={series}
            type='boxPlot'
            height={350}
          />
        </Card>
      ) : (
        renderTableView()
      )}

      {/* Modal para ver todos los clientes */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size='2xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <h3>{title} - Todos los Clientes</h3>
          </ModalHeader>
          <ModalBody>
            <div className='space-y-4'>
              <Input
                placeholder='Buscar cliente...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full'
              />

              <Table aria-label='Tabla de costos por cliente'>
                <TableHeader>
                  <TableColumn
                    className='cursor-pointer'
                    onClick={() => {
                      if (sortBy === 'label') {
                        setSortDirection(
                          sortDirection === 'asc' ? 'desc' : 'asc'
                        );
                      } else {
                        setSortBy('label');
                        setSortDirection('asc');
                      }
                    }}
                  >
                    Cliente{' '}
                    {sortBy === 'label' &&
                      (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableColumn>
                  <TableColumn
                    className='cursor-pointer'
                    onClick={() => {
                      if (sortBy === 'value') {
                        setSortDirection(
                          sortDirection === 'asc' ? 'desc' : 'asc'
                        );
                      } else {
                        setSortBy('value');
                        setSortDirection('desc');
                      }
                    }}
                  >
                    Valor{' '}
                    {sortBy === 'value' &&
                      (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableColumn>
                  <TableColumn
                    className='cursor-pointer'
                    onClick={() => {
                      if (sortBy === 'percentile') {
                        setSortDirection(
                          sortDirection === 'asc' ? 'desc' : 'asc'
                        );
                      } else {
                        setSortBy('percentile');
                        setSortDirection('desc');
                      }
                    }}
                  >
                    Percentil{' '}
                    {sortBy === 'percentile' &&
                      (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableColumn>
                  <TableColumn
                    className='cursor-pointer'
                    onClick={() => {
                      if (sortBy === 'kilos') {
                        setSortDirection(
                          sortDirection === 'asc' ? 'desc' : 'asc'
                        );
                      } else {
                        setSortBy('kilos');
                        setSortDirection('desc');
                      }
                    }}
                  >
                    Kilos{' '}
                    {sortBy === 'kilos' &&
                      (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableColumn>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.label}</TableCell>
                      <TableCell>
                        <strong>{formatUnit(item.value)}</strong>
                      </TableCell>
                      <TableCell>
                        <span
                          style={{ color: COLORS.cherry, fontWeight: 'bold' }}
                        >
                          {item.percentile ?? '-'}%
                        </span>
                      </TableCell>
                      <TableCell>{item.kilos.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={() => setIsModalOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CostDispertionChart;
